import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw } from "vue-router";

/*
  `?nonav`: the Operator with nothing on screen that leads anywhere else — no rail, no process
  header, no way back to the list. For embedding a dataspace in another page, or a kiosk.

  It lives in the URL and nowhere else. Nothing is stored, so it ends with the tab, and a second
  tab without it is the ordinary Operator.
*/

/** Whether a query asks for `nonav`. vue-router reads a bare `?nonav` as `{ nonav: null }`, so the test is the key, not its value. */
export function hasNoNav(query: LocationQuery): boolean {
	return "nonav" in query;
}

/**
 * The redirect that keeps `nonav` across a navigation inside the Operator, or nothing.
 *
 * Every link and push in the Operator names a route without its query, so without this the first
 * click — opening a process, the start page sending to the list — would bring the navigation back.
 * Carried only into the Operator: a route anywhere else (the sign-in page, the Designer) has no
 * chrome to hide, and a target that already has it needs no redirect, which is also what stops
 * this from looping. No `replace` of its own: a redirect keeps the navigation's, so a push that is
 * carried is still a push and Back still returns to the page it left.
 */
export function carryNoNav(
	to: Pick<RouteLocationNormalized, "query" | "matched" | "path" | "hash">,
	from: Pick<RouteLocationNormalized, "query">,
): RouteLocationRaw | undefined {
	if (!hasNoNav(from.query) || hasNoNav(to.query)) return undefined;
	if (!to.matched.some((record) => record.meta.section === "operator")) return undefined;
	return { path: to.path, query: { ...to.query, nonav: null }, hash: to.hash };
}
